import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getZones,
  getZone,
  createZone,
  updateZone,
  deleteZone,
  uploadZonePhoto,
  deleteZonePhoto,
  getLocations,
  getLocation,
  getLocationSummary,
  createLocation,
  updateLocation,
  deleteLocation,
  bulkCreateLocations,
} from '../api';
import type {
  CreateZoneInput,
  UpdateZoneInput,
  CreateLocationInput,
  UpdateLocationInput,
} from '../types';

// Helper to extract error message
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// Query keys
export const wmsLocationKeys = {
  all: ['wms-locations'] as const,
  zones: () => [...wmsLocationKeys.all, 'zones'] as const,
  zone: (id: string) => [...wmsLocationKeys.zones(), id] as const,
  locations: (filters?: Record<string, unknown>) => [...wmsLocationKeys.all, 'locations', filters] as const,
  location: (id: string) => [...wmsLocationKeys.all, 'location', id] as const,
  summary: () => [...wmsLocationKeys.all, 'summary'] as const,
};

// ============================================================================
// Zone Hooks
// ============================================================================

export function useZones() {
  return useQuery({
    queryKey: wmsLocationKeys.zones(),
    queryFn: getZones,
  });
}

export function useZone(id: string) {
  return useQuery({
    queryKey: wmsLocationKeys.zone(id),
    queryFn: () => getZone(id),
    enabled: !!id,
  });
}

export function useCreateZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateZoneInput) => createZone(data),
    onSuccess: (zone) => {
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zones() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success(`Zone "${zone.code}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create zone', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateZoneInput }) => updateZone(id, data),
    onSuccess: (zone, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zones() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zone(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success(`Zone "${zone.code}" updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update zone', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteZone() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteZone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zones() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success('Zone deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete zone', { description: getErrorMessage(error) });
    },
  });
}

export function useUploadZonePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zoneId, file }: { zoneId: string; file: File }) =>
      uploadZonePhoto(zoneId, file),
    onSuccess: (_zone, variables) => {
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zones() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zone(variables.zoneId) });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success('Zone photo uploaded');
    },
    onError: (error) => {
      toast.error('Failed to upload zone photo', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteZonePhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (zoneId: string) => deleteZonePhoto(zoneId),
    onSuccess: (_result, zoneId) => {
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zones() });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.zone(zoneId) });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success('Zone photo removed');
    },
    onError: (error) => {
      toast.error('Failed to delete zone photo', { description: getErrorMessage(error) });
    },
  });
}

// ============================================================================
// Location Hooks
// ============================================================================

interface UseLocationsOptions {
  zone_id?: string;
  bin_type?: string;
  status?: string;
  search?: string;
  page_number?: number;
  page_length?: number;
  enabled?: boolean;
}

export function useLocations(options: UseLocationsOptions = {}) {
  const { enabled, ...queryOptions } = options;
  return useQuery({
    queryKey: wmsLocationKeys.locations(queryOptions as Record<string, unknown>),
    queryFn: () => getLocations({
      zoneId: queryOptions.zone_id,
      status: queryOptions.status,
      search: queryOptions.search,
      pageNumber: queryOptions.page_number,
      pageLength: queryOptions.page_length,
    }),
    enabled: enabled ?? true,
  });
}

export function useLocation(id: string) {
  return useQuery({
    queryKey: wmsLocationKeys.location(id),
    queryFn: () => getLocation(id),
    enabled: !!id,
  });
}

export function useLocationSummary() {
  return useQuery({
    queryKey: wmsLocationKeys.summary(),
    queryFn: getLocationSummary,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateLocationInput) => createLocation(data),
    onSuccess: (location) => {
      // Use partial key to invalidate ALL location queries regardless of filters
      queryClient.invalidateQueries({ queryKey: [...wmsLocationKeys.all, 'locations'] });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success(`Location "${location.bin_code}" created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create location', { description: getErrorMessage(error) });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateLocationInput }) => updateLocation(id, data),
    onSuccess: (location, variables) => {
      // Use partial key to invalidate ALL location queries regardless of filters
      queryClient.invalidateQueries({ queryKey: [...wmsLocationKeys.all, 'locations'] });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.location(variables.id) });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success(`Location "${location.bin_code}" updated successfully`);
    },
    onError: (error) => {
      toast.error('Failed to update location', { description: getErrorMessage(error) });
    },
  });
}

export function useDeleteLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteLocation(id),
    onSuccess: () => {
      // Use partial key to invalidate ALL location queries regardless of filters
      queryClient.invalidateQueries({ queryKey: [...wmsLocationKeys.all, 'locations'] });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success('Location deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete location', { description: getErrorMessage(error) });
    },
  });
}

export function useBulkCreateLocations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ zoneId, binCodes }: { zoneId: string; binCodes: string[] }) => bulkCreateLocations(zoneId, binCodes),
    onSuccess: (result) => {
      // Use partial key to invalidate ALL location queries regardless of filters
      queryClient.invalidateQueries({ queryKey: [...wmsLocationKeys.all, 'locations'] });
      queryClient.invalidateQueries({ queryKey: wmsLocationKeys.summary() });
      toast.success(`${result.created} locations created successfully`);
    },
    onError: (error) => {
      toast.error('Failed to create locations', { description: getErrorMessage(error) });
    },
  });
}
